import { MoreVertical, Pencil, Trash2, Webhook, Users, History } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { DropdownMenu } from '../ui/DropdownMenu';

export interface ProductCardProps {
  product: any;
  onEdit: (product: any) => void;
  onDelete: (productId: string) => void;
  onManageTenants: (product: any) => void;
  onEnableWebhook: (productId: string) => void;
  onDisableWebhook: (productId: string) => void;
  onViewHistory: (product: any) => void;
  canManage: boolean;
  canManageWebhooks: boolean;
  canViewHistory: boolean;
}

function shortenUrl(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/^\//, '').replace(/\.git$/, '');
    return path.length > 40 ? path.slice(0, 37) + '...' : path;
  } catch {
    return url.length > 40 ? url.slice(0, 37) + '...' : url;
  }
}

export function ProductCard({
  product,
  onEdit,
  onDelete,
  onManageTenants,
  onEnableWebhook,
  onDisableWebhook,
  onViewHistory,
  canManage,
  canManageWebhooks,
  canViewHistory,
}: ProductCardProps) {
  const webhookEnabled = product?.webhook?.enabled === true;
  const associations = product?.tenantAssociations || [];
  const repos = product?.repos || [];
  const envCount = associations.reduce((sum: number, a: any) => sum + Object.keys(a?.environments || {}).length, 0);

  const menuItems = [
    ...(canManage
      ? [{
          label: 'Edit',
          icon: <Pencil className="w-4 h-4" />,
          onClick: () => onEdit(product),
        }]
      : []),
    ...(canManage
      ? [{
          label: 'Target Environments',
          icon: <Users className="w-4 h-4" />,
          onClick: () => onManageTenants(product),
        }]
      : []),
    ...(canManageWebhooks
      ? [{
          label: webhookEnabled ? 'Disable Webhooks' : 'Enable Webhooks',
          icon: <Webhook className="w-4 h-4" />,
          onClick: () => (webhookEnabled ? onDisableWebhook(product.id) : onEnableWebhook(product.id)),
        }]
      : []),
    ...(canViewHistory
      ? [{
          label: 'View History',
          icon: <History className="w-4 h-4" />,
          onClick: () => onViewHistory(product),
        }]
      : []),
    ...(canManage
      ? [{
          label: 'Delete',
          icon: <Trash2 className="w-4 h-4" />,
          onClick: () => onDelete(product.id),
          className: 'text-red-600 dark:text-red-400',
        }]
      : []),
  ];

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {product?.id || 'Unknown'}
            </h3>
            {product?.source === 'tenant' ? (
              <Badge variant="outline" className="text-[10px]">Tenant</Badge>
            ) : product?.source === 'master' ? (
              <Badge variant="outline" className="text-[10px]">Common</Badge>
            ) : null}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
            {product?.workspaceDir || 'No workspace dir'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {menuItems.length > 0 && (
            <DropdownMenu
              trigger={
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              }
              items={menuItems}
              align="end"
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {webhookEnabled ? (
          <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            Webhooks Active
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs">Webhooks Inactive</Badge>
        )}
        <Badge variant="outline" className="text-xs">{repos.length} Repo{repos.length === 1 ? '' : 's'}</Badge>
        <Badge variant="outline" className="text-xs">{associations.length} Tenant{associations.length === 1 ? '' : 's'}</Badge>
        <Badge variant="outline" className="text-xs">{envCount} Env{envCount === 1 ? '' : 's'}</Badge>
      </div>

      {/* Repos */}
      {repos.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Repositories</p>
          <div className="space-y-1.5">
            {repos.slice(0, 2).map((repo: any, i: number) => (
              <div key={i} className="text-sm text-gray-700 dark:text-gray-300 truncate" title={repo.repoUrl}>
                <span className="font-medium text-gray-900 dark:text-gray-100">{repo.name}</span>
                <span className="text-gray-400 mx-1">·</span>
                {shortenUrl(repo.repoUrl)}
                <span className="text-gray-400 ml-1">({repo.branch || 'develop'})</span>
              </div>
            ))}
            {repos.length > 2 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">+{repos.length - 2} more repos</p>
            )}
          </div>
        </div>
      )}

      {/* Tenants & Environments */}
      {associations.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Target Environments</p>
          <div className="flex items-center gap-2 flex-wrap">
          {associations.map((a: any) => (
            <div key={a.tenantCode} className="flex items-center gap-1">
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                {a.tenantCode}
              </Badge>
              {a.environments && Object.keys(a.environments).length > 0 && (
                <>
                  {Object.entries(a.environments).map(([envName, envConfig]: [string, any]) => {
                    const ep = envConfig?.envProducts?.[0];
                    const deployPr = ep?.deployPrEnv;
                    return (
                      <Badge key={envName} variant="outline" className="text-xs">
                        {envName}
                        {deployPr ? (
                          <span className="ml-1 text-green-600 dark:text-green-400" title="PR env enabled">PR</span>
                        ) : (
                          <span className="ml-1 text-blue-600 dark:text-blue-400" title="Deploy enabled">●</span>
                        )}
                      </Badge>
                    );
                  })}
                </>
              )}
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Footer info */}
      {product?.prEnvTTLMinutes != null && (
        <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PR env TTL: {product.prEnvTTLMinutes} min
          </p>
        </div>
      )}
    </div>
  );
}
